import { useState, useEffect } from "react";
import { AppSidebar } from "./components/AppSidebar";
import { ChatInterface } from "./components/ChatInterface";
import { Infrastructure } from "./components/Infrastructure";
import { Settings } from "./components/Settings";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Menu, Bell, Clock, Download } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "./components/ui/sheet";
import { getBackendUrl } from "./src/utils/api";
import { ErrorBoundary } from "./components/ErrorBoundary";

type ActiveView = "chat" | "analysis" | "evidence" | "reports" | "activity" | "settings";

interface CaseData {
  id: string;
  title: string;
  type: string;
  urls: string[];
  createdAt: Date;
  status: "active" | "completed" | "pending";
}

interface Message {
  id: string;
  type: "user" | "assistant" | "system" | "error";
  content: string;
  timestamp: Date;
  metadata?: {
    confidence?: number;
    jurisdiction?: string;
    urgency?: "low" | "medium" | "high";
    actionItems?: string[];
    urlAnalysis?: {
      hostname: string;
      hosting: string;
      monetization: string[];
      jurisdiction: string;
    };
  };
  // LLM API Response data
  llmResponse?: {
    context_loaded: boolean;
    sources_used: Array<{
      filename: string;
      content_type: string;
      relevance_score: number;
    }>;
    package_info?: {
      client_name: string;
      case_name: string;
      domains_analyzed: string[];
      files_available: string[] | number;
    };
  };
  error?: string;
}

function App() {
  const [activeView, setActiveView] = useState<ActiveView>("chat");
  const [isDark, setIsDark] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cases, setCases] = useState<CaseData[]>([]);
  const [activeCase, setActiveCase] = useState<CaseData | null>(null);
  const [caseChatSessions, setCaseChatSessions] = useState<Record<string, Message[]>>({});
  const [isDownloadingEvidence, setIsDownloadingEvidence] = useState(false);

  // Set dark mode as default and persist theme
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const prefersDark = savedTheme === "dark" || (!savedTheme && true); // Default to dark
    
    setIsDark(prefersDark);
    
    if (prefersDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  // Load cases from API on mount
  useEffect(() => {
    const fetchCases = async () => {
      try {
        const response = await fetch(`${getBackendUrl()}/api/cases`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        // Transform API response to CaseData objects
        const transformedCases: CaseData[] = data.cases.map((caseItem: { case_name: string; url_count: number; case_type?: string }) => ({
          id: caseItem.case_name,
          title: caseItem.case_name.charAt(0).toUpperCase() + caseItem.case_name.slice(1), // Capitalize first letter
          type: caseItem.case_type ? caseItem.case_type : "Investigation", // Use backend case_type or default to "Investigation"
          urls: new Array(caseItem.url_count).fill(''), // Create array with correct length for URL count display
          createdAt: new Date(), // Default to current date
          status: "active" as const
        }));
        
        setCases(transformedCases);
        
        // Set the first case as active by default if no active case is saved
        const savedActiveCase = localStorage.getItem("prune-active-case");
        if (savedActiveCase) {
          const activeId = JSON.parse(savedActiveCase);
          const foundCase = transformedCases.find((c: CaseData) => c.id === activeId);
          if (foundCase) {
            setActiveCase(foundCase);
          }
        } else if (transformedCases.length > 0) {
          setActiveCase(transformedCases[0]);
        }
      } catch (error) {
        console.error("Failed to fetch cases:", error);
        // Fall back to localStorage if API fails
        const savedCases = localStorage.getItem("prune-cases");
        if (savedCases) {
          const parsedCases = JSON.parse(savedCases).map((c: any) => ({
            ...c,
            createdAt: new Date(c.createdAt)
          }));
          setCases(parsedCases);
        }
      }
    };

    fetchCases();

    // Load chat sessions from localStorage for now
    const savedSessions = localStorage.getItem("prune-chat-sessions");
    if (savedSessions) {
      const parsedSessions = JSON.parse(savedSessions);
      // Convert timestamp strings back to Date objects
      Object.keys(parsedSessions).forEach(caseId => {
        parsedSessions[caseId] = parsedSessions[caseId].map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      });
      setCaseChatSessions(parsedSessions);
    }
  }, []);

  // Save to localStorage when cases or sessions change
  useEffect(() => {
    if (cases.length > 0) {
      localStorage.setItem("prune-cases", JSON.stringify(cases));
    }
  }, [cases]);

  useEffect(() => {
    if (Object.keys(caseChatSessions).length > 0) {
      localStorage.setItem("prune-chat-sessions", JSON.stringify(caseChatSessions));
    }
  }, [caseChatSessions]);

  useEffect(() => {
    if (activeCase) {
      localStorage.setItem("prune-active-case", JSON.stringify(activeCase.id));
    }
  }, [activeCase]);

  const toggleTheme = (newIsDark: boolean) => {
    setIsDark(newIsDark);
    localStorage.setItem("theme", newIsDark ? "dark" : "light");
    
    if (newIsDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  const handleCreateCase = async (caseData: { title: string; type: string; urls: string[] }) => {
    try {
      // Call the supabase-full-flow endpoint
      const supabaseResponse = await fetch('http://localhost:8000/api/supabase-full-flow', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url_groups: [caseData.urls], // Wrap URLs array in another array
          case_name: caseData.title,
          case_type: caseData.type
        })
      });

      if (!supabaseResponse.ok) {
        throw new Error(`Failed to create case in supabase-full-flow: ${supabaseResponse.status}`);
      }

      console.log('✅ Successfully created case in supabase-full-flow');

      // If backend call succeeds, create the case locally
      const newCase: CaseData = {
        id: `case-${Date.now()}`,
        title: caseData.title,
        type: caseData.type,
        urls: caseData.urls,
        createdAt: new Date(),
        status: "active"
      };

      setCases(prev => [newCase, ...prev]);
      setActiveCase(newCase);

      // Generate initial URL analysis summary
      const urlAnalysisSummary = generateUrlAnalysisSummary(caseData.urls);
      
      // Initialize chat session with URL analysis
      const initialMessages: Message[] = [
        {
          id: "system-welcome",
          type: "system",
          content: `**Case ${newCase.title} Created**\n\nCase Type: ${getCaseTypeLabel(newCase.type)}\nURLs to Analyze: ${newCase.urls.length}\nCreated: ${newCase.createdAt.toLocaleString()}`,
          timestamp: new Date(Date.now() - 2000)
        },
        {
          id: "analysis-summary",
          type: "assistant",
          content: urlAnalysisSummary,
          timestamp: new Date(Date.now() - 1000),
          metadata: {
            confidence: 85,
            jurisdiction: "Multi-jurisdictional",
            urgency: newCase.type.includes("csam") ? "high" : "medium"
          }
        },
        {
          id: "assistant-ready",
          type: "assistant", 
          content: "I've completed the initial analysis of your target URLs. What would you like me to help you with next? You can use the action buttons below or ask me anything about this investigation.",
          timestamp: new Date(),
          metadata: {
            confidence: 100,
            urgency: "low"
          }
        }
      ];

      setCaseChatSessions(prev => ({
        ...prev,
        [newCase.id]: initialMessages
      }));

      // Switch to chat view
      setActiveView("chat");
    } catch (error) {
      console.error('Error creating case:', error);
      // You might want to show an error message to the user here
      alert('Failed to create case. Please try again.');
      return;
    }
  };

  const handleDeleteCase = async (caseId: string) => {
    try {
      // Call the DELETE endpoint
      const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete case: ${response.status}`);
      }

      console.log('✅ Successfully deleted case from backend');

      // Remove case from local state
      setCases(prev => prev.filter(c => c.id !== caseId));
      
      // If this was the active case, switch to first available case or null
      if (activeCase?.id === caseId) {
        const remainingCases = cases.filter(c => c.id !== caseId);
        setActiveCase(remainingCases.length > 0 ? remainingCases[0] : null);
        localStorage.removeItem("prune-active-case");
      }

      // Remove chat sessions for this case
      setCaseChatSessions(prev => {
        const updated = { ...prev };
        delete updated[caseId];
        return updated;
      });

      console.log('✅ Successfully removed case from local state');
    } catch (error) {
      console.error('Error deleting case:', error);
      throw error; // Re-throw so the sidebar can handle the error
    }
  };

  const generateUrlAnalysisSummary = (urls: string[]): string => {
    const domains = urls.map(url => {
      try {
        return new URL(url).hostname;
      } catch {
        return url;
      }
    });
    
    const uniqueDomains = [...new Set(domains)];
    const totalUrls = urls.length;
    
    // Mock analysis data - in real app this would come from actual analysis
    const mockHostingProviders = ["CloudFlare", "AWS", "DigitalOcean", "Namecheap"];
    const mockMonetization = ["Bitcoin payments", "Credit card processing", "Affiliate networks", "Cryptocurrency addresses"];
    const mockJurisdictions = ["Netherlands", "United States", "Germany", "Romania"];
    
    const randomHosting = mockHostingProviders[Math.floor(Math.random() * mockHostingProviders.length)];
    const randomMonetization = mockMonetization.slice(0, Math.floor(Math.random() * 3) + 1);
    const randomJurisdiction = mockJurisdictions[Math.floor(Math.random() * mockJurisdictions.length)];
    
    return `## Initial URL Analysis Complete

**Infrastructure Analysis:**
• **${totalUrls} URLs** analyzed across **${uniqueDomains.length} unique domains**
• Primary hosting: **${randomHosting}** infrastructure
• Geographic distribution: **${randomJurisdiction}** and related jurisdictions
• SSL certificates: Mixed validity (some expired/self-signed detected)

**Monetization Patterns:**
${randomMonetization.map(m => `• ${m}`).join('\n')}

**Key Domains Identified:**
${uniqueDomains.slice(0, 3).map(domain => `• \`${domain}\``).join('\n')}
${uniqueDomains.length > 3 ? `• ...and ${uniqueDomains.length - 3} more domains` : ''}

**Recommended Next Steps:**
1. Issue preservation notices to hosting providers
2. Coordinate with ${randomJurisdiction} law enforcement
3. Begin infrastructure mapping for related domains
4. Document payment processing chains

*This analysis was generated automatically. Use the action buttons below to proceed with specific legal actions.*`;
  };

  const getCaseTypeLabel = (type: string): string => {
    const types: Record<string, string> = {
      "ncii": "NCII (Non-consensual intimate images)",
      "verified-csam": "Verified CSAM",
      "suspected-csam": "Suspected CSAM", 
      "verified-trafficking": "Verified Human Trafficking",
      "test-case": "Test Case"
    };
    return types[type] || type;
  };

  const getCaseTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      "ncii": "bg-purple-500/10 text-purple-400 border-purple-500/20",
      "verified-csam": "bg-red-500/10 text-red-400 border-red-500/20",
      "suspected-csam": "bg-orange-500/10 text-orange-400 border-orange-500/20",
      "verified-trafficking": "bg-red-600/10 text-red-500 border-red-600/20",
      "test-case": "bg-blue-500/10 text-blue-400 border-blue-500/20"
    };
    return colors[type] || "bg-gray-500/10 text-gray-400 border-gray-500/20";
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "active": return "bg-green-500 shadow-green-500/50";
      case "completed": return "bg-blue-500 shadow-blue-500/50";
      case "pending": return "bg-yellow-500 shadow-yellow-500/50";
      default: return "bg-gray-500 shadow-gray-500/50";
    }
  };

  const handleSwitchCase = (caseData: CaseData) => {
    setActiveCase(caseData);
    setActiveView("chat");
  };

  const updateCaseMessages = (caseId: string, messages: Message[]) => {
    setCaseChatSessions(prev => ({
      ...prev,
      [caseId]: messages
    }));
  };

  const handleDownloadEvidence = async () => {
    if (!activeCase) {
      console.error('No active case for evidence download');
      return;
    }

    setIsDownloadingEvidence(true);
    
    try {
      const response = await fetch(`${getBackendUrl()}/api/cases/${activeCase.id}/download`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to download evidence: ${response.status} ${response.statusText}`);
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename from response header or use default
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `${activeCase.id}_evidence.zip`;
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      console.log('✅ Evidence download completed successfully');
    } catch (error) {
      console.error('❌ Error downloading evidence:', error);
      alert('Failed to download evidence. Please try again.');
    } finally {
      setIsDownloadingEvidence(false);
    }
  };

  const renderActiveView = () => {
    switch (activeView) {
      case "chat":
        if (!activeCase) {
          return (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto">
                  <div className="w-8 h-8 bg-gray-400/20 rounded" />
                </div>
                <div>
                  <h3>No Active Case</h3>
                  <p className="text-muted-foreground">Create a new investigation to start chatting with the AI assistant</p>
                </div>
                <Button 
                  variant="outline"
                  onClick={() => {
                    // This will be handled by the AppSidebar component
                  }}
                >
                  Create New Case
                </Button>
              </div>
            </div>
          );
        }
        return (
          <ErrorBoundary>
            <ChatInterface 
              activeCase={activeCase}
              messages={caseChatSessions[activeCase.id] || []}
              onUpdateMessages={(messages) => updateCaseMessages(activeCase.id, messages)}
            />
          </ErrorBoundary>
        );
      case "analysis":
        return <Infrastructure activeCase={activeCase || undefined} />;
      case "evidence":
        if (!activeCase) {
          return (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto">
                  <Download className="w-8 h-8 text-muted-foreground" />
                </div>
                <div>
                  <h3>Evidence Download</h3>
                  <p className="text-muted-foreground">No active case selected</p>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mx-auto">
                <Download className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h3>Evidence Download</h3>
                <p className="text-muted-foreground">
                  Download all evidence files for case <strong>{activeCase.title}</strong>
                </p>
              </div>
              <Button 
                onClick={handleDownloadEvidence}
                disabled={isDownloadingEvidence}
                className="bg-primary hover:bg-primary/90"
              >
                {isDownloadingEvidence ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download Evidence
                  </>
                )}
              </Button>
            </div>
          </div>
        );
      case "reports":
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto">
                <div className="w-8 h-8 bg-gray-400/20 rounded" />
              </div>
              <div>
                <h3>Investigation Reports</h3>
                <p className="text-muted-foreground">Generate comprehensive case reports</p>
              </div>
              <Button variant="outline">Coming Soon</Button>
            </div>
          </div>
        );
      case "activity":
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto">
                <Bell className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <h3>Case Activity</h3>
                <p className="text-muted-foreground">
                  {activeCase ? `Activity log for case ${activeCase.title}` : "View case activity and notifications"}
                </p>
              </div>
              <Button variant="outline">Coming Soon</Button>
            </div>
          </div>
        );
      case "settings":
        return <Settings isDark={isDark} onThemeChange={toggleTheme} />;
      default:
        return (
          <ErrorBoundary>
            <ChatInterface 
              activeCase={activeCase!}
              messages={activeCase ? caseChatSessions[activeCase.id] || [] : []}
              onUpdateMessages={activeCase ? (messages) => updateCaseMessages(activeCase.id, messages) : () => {}}
            />
          </ErrorBoundary>
        );
    }
  };

  // Dynamic navigation items based on active case
  const getNavigationItems = (): Array<{ id: string; label: string; badge?: string }> => {
    const baseItems = [
      { id: "chat", label: "Chat", badge: "AI" },
    ];

    // Add case-specific navigation items only when there's an active case
    if (activeCase) {
      const urlLabel = activeCase.urls.length > 1 ? "Infrastructure" : "Infrastructure";
      baseItems.push(
        { id: "analysis", label: urlLabel, badge: activeCase.urls.length.toString() },
        { id: "evidence", label: "Evidence", badge: "" },
        { id: "reports", label: "Reports", badge: "" },
        { id: "activity", label: "Activity", badge: "" }
      );
    }

    return baseItems;
  };

  const navigationItems = getNavigationItems();

  const MobileNavigation = () => (
    <div className="md:hidden border-b border-border bg-background">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <div className="w-4 h-4 bg-primary-foreground rounded" />
          </div>
          <div>
            <h1 className="font-semibold">Prune Copilot</h1>
            {activeCase && (
              <p className="text-xs text-muted-foreground">Case: {activeCase.title}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" aria-label="Open navigation menu">
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation Menu</SheetTitle>
                <SheetDescription>
                  Access Prune Copilot features including cases, chat, and analysis tools.
                </SheetDescription>
              </SheetHeader>
              <AppSidebar 
                cases={cases}
                activeCase={activeCase}
                onCreateCase={handleCreateCase}
                onSwitchCase={handleSwitchCase}
                onDeleteCase={handleDeleteCase}
                onOpenSettings={() => setActiveView("settings")}
              />
            </SheetContent>
          </Sheet>
        </div>
      </div>
      
      {/* Mobile case header */}
      {activeCase && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2">
            <Badge className={getCaseTypeColor(activeCase.type)} variant="outline">
              {getCaseTypeLabel(activeCase.type).split(' ')[0]}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {activeCase.urls.length} URLs
            </span>
            <div className={`w-2 h-2 rounded-full ${getStatusColor(activeCase.status)}`} 
                 title={`Status: ${activeCase.status}`} />
          </div>
        </div>
      )}
      
      {/* Mobile tab navigation */}
      <div className="flex overflow-x-auto px-4 pb-2">
        {navigationItems.map((item) => (
          <Button
            key={item.id}
            variant={activeView === item.id ? "default" : "ghost"}
            size="sm"
            className="whitespace-nowrap mr-2"
            onClick={() => setActiveView(item.id as ActiveView)}
            disabled={!activeCase && item.id !== "chat"}
          >
            {item.label}
            {item.badge && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-accent/50 rounded">
                {item.badge}
              </span>
            )}
          </Button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-background text-foreground">
      {/* Mobile Navigation */}
      <MobileNavigation />
      
      {/* Desktop Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden md:block">
          <div className="h-full">
            <AppSidebar 
              cases={cases}
              activeCase={activeCase}
              onCreateCase={handleCreateCase}
              onSwitchCase={handleSwitchCase}
              onDeleteCase={handleDeleteCase}
              onOpenSettings={() => setActiveView("settings")}
            />
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Desktop Case Header - NEW: Added above navigation */}
          {activeCase && (
            <div className="hidden md:block p-4 border-b border-border bg-background">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h1>{activeCase.title}</h1>
                  <div className="flex items-center gap-2">
                    <Badge className={getCaseTypeColor(activeCase.type)} variant="outline">
                      {getCaseTypeLabel(activeCase.type).split(' (')[0]}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {activeCase.urls.length} URLs
                    </span>
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(activeCase.status)}`} 
                         title={`Status: ${activeCase.status}`} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Created {activeCase.createdAt.toLocaleDateString()}
                  </div>
                  <div className="w-2 h-2 rounded-full bg-green-500 shadow-sm shadow-green-500/50" 
                       title="Active" />
                </div>
              </div>
            </div>
          )}
          
          {/* Desktop Navigation - Now below case header */}
          <div className="hidden md:flex items-center justify-between p-4 border-b border-border bg-background/95 backdrop-blur-sm">
            <div className="flex items-center gap-1">
              {navigationItems.map((item) => (
                <Button
                  key={item.id}
                  variant={activeView === item.id ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveView(item.id as ActiveView)}
                  disabled={!activeCase && item.id !== "chat"}
                  className="relative transition-all duration-200"
                >
                  {item.id === "activity" && <Bell className="w-4 h-4 mr-2" />}
                  {item.label}
                  {item.badge && (
                    <span className="ml-2 px-1.5 py-0.5 text-xs bg-accent/50 rounded">
                      {item.badge}
                    </span>
                  )}
                </Button>
              ))}
            </div>
            
            {/* Right side content - now empty since theme toggle moved to settings */}
            <div className="flex items-center gap-4">
              {/* Can add other controls here if needed */}
            </div>
          </div>
          
          {/* Content Area */}
          <div className="flex-1 overflow-hidden bg-background">
            {renderActiveView()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;